import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE, WEBSITE_URL, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { getAffiliatePageStyles } from './affiliateStyles';
import './affiliates.css';
import AffiliateLinkGenerator from './AffiliateLinkGenerator';
import { buildAffiliateLink, copyTextToClipboard } from '../../utils/affiliateLinks';

const orderStatusColor = {
  pending: '#fbbf24',
  confirmed: '#60a5fa',
  processing: '#a78bfa',
  outForDelivery: '#22d3ee',
  delivered: '#4ade80',
  cancelled: '#f87171',
  returned: '#ef4444',
};

const MONTHS = [
  { value: '', label: 'All Months' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

function getDaysInMonth(year, month) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

function getLatestYear() {
  return new Date().getFullYear();
}

function buildYearOptions(latestYear = getLatestYear(), startYear = 2024) {
  const years = [];
  for (let year = latestYear; year >= startYear; year -= 1) {
    years.push(String(year));
  }
  return years;
}

export default function AffiliateDetails() {
  const { id } = useParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('active');
  const [saving, setSaving] = useState(false);
  const [filterYear, setFilterYear] = useState(() => String(getLatestYear()));
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDay, setFilterDay] = useState('');

  const styles = getAffiliatePageStyles(isMobile, isSmallMobile);
  const yearOptions = buildYearOptions(getLatestYear());
  const maxDays = getDaysInMonth(filterYear, filterMonth);
  const dayOptions = useMemo(
    () => Array.from({ length: maxDays }, (_, index) => String(index + 1)),
    [maxDays]
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear) params.set('year', filterYear);
      if (filterMonth) params.set('month', filterMonth);
      if (filterDay) params.set('day', filterDay);

      const query = params.toString();
      const response = await fetch(
        `${API_BASE}/v1/affiliate/${id}${query ? `?${query}` : ''}`
      );
      const result = await parseJsonResponse(response);
      setData(result.data);
      setStatus(result.data.affiliate.status);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to load affiliate details. Please try again later.'));
    } finally {
      setLoading(false);
    }
  }, [id, filterYear, filterMonth, filterDay]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const updateStatus = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/v1/affiliate/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      await parseJsonResponse(response);
      toast.success('Affiliate status updated');
      fetchDetails();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to update status. Please try again later.'));
    } finally {
      setSaving(false);
    }
  };

  const copyStoreLink = async (code) => {
    try {
      await copyTextToClipboard(buildAffiliateLink(code, WEBSITE_URL));
      toast.success('Store affiliate link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const clearFilters = () => {
    setFilterYear(String(getLatestYear()));
    setFilterMonth('');
    setFilterDay('');
  };

  const handleYearChange = (value) => {
    setFilterYear(value);
    if (filterDay && filterMonth && Number(filterDay) > getDaysInMonth(value, filterMonth)) {
      setFilterDay('');
    }
  };

  const handleMonthChange = (value) => {
    setFilterMonth(value);
    if (!value) {
      setFilterDay('');
    } else if (filterDay && Number(filterDay) > getDaysInMonth(filterYear, value)) {
      setFilterDay('');
    }
  };

  if (loading && !data) {
    return (
      <div style={styles.mainWrapper}>
        <div style={styles.container}>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading affiliate details...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.mainWrapper}>
        <div style={styles.container}>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Affiliate not found.</p>
        </div>
      </div>
    );
  }

  const { affiliate, orders, stats } = data;
  const affiliateLink = buildAffiliateLink(affiliate.code, WEBSITE_URL);

  return (
    <div style={styles.mainWrapper}>
      <Toaster position="top-center" />
      <div style={styles.container}>
        <h1 style={styles.headerTitle}>{affiliate.name}</h1>
        <p style={styles.breadcrumbs}>Home • Affiliates • Details</p>

        <div style={{ ...styles.contentContainer, marginTop: '20px' }}>
          <div style={styles.profileCard}>
            <div style={styles.profileTitle}>Affiliate Profile</div>
            <p style={styles.profileRow}><strong>Email:</strong> {affiliate.email}</p>
            <p style={styles.profileRow}><strong>Phone:</strong> {affiliate.phone || '—'}</p>
            <p style={styles.profileRow}><strong>Code:</strong> {affiliate.code}</p>
            <p style={styles.profileRow}><strong>Link:</strong> {affiliateLink}</p>
            <p style={styles.profileRow}><strong>Commission:</strong> {affiliate.commissionRate}%</p>
            <p style={styles.profileRow}>
              <strong>Customer Discount:</strong>{' '}
              {affiliate.discountType === 'none'
                ? 'None'
                : affiliate.discountType === 'percentage'
                  ? `${affiliate.discountValue}%`
                  : `Rs ${affiliate.discountValue}`}
            </p>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="affiliate-form-input"
                style={{ maxWidth: '180px' }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
              <button
                type="button"
                onClick={updateStatus}
                disabled={saving}
                style={styles.primaryButton}
              >
                {saving ? 'Saving...' : 'Update Status'}
              </button>
              <button
                type="button"
                onClick={() => copyStoreLink(affiliate.code)}
                style={styles.secondaryButton}
              >
                Copy Store Link
              </button>
            </div>
          </div>

          <div style={styles.profileCard}>
            <div style={styles.profileTitle}>Promotion Links</div>
            <p style={{ ...styles.profileRow, marginBottom: '14px' }}>
              Share the store link or pick a product for a direct promotion link.
            </p>
            <AffiliateLinkGenerator affiliateCode={affiliate.code} />
          </div>

          <div className="affiliate-order-filters">
            <div className="affiliate-order-filter-group">
              <span className="affiliate-order-filter-label">Year</span>
              <select
                className="affiliate-form-input"
                value={filterYear}
                onChange={(e) => handleYearChange(e.target.value)}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="affiliate-order-filter-group">
              <span className="affiliate-order-filter-label">Month</span>
              <select
                className="affiliate-form-input"
                value={filterMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                {MONTHS.map((month) => (
                  <option key={month.label} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <div className="affiliate-order-filter-group">
              <span className="affiliate-order-filter-label">Day</span>
              <select
                className="affiliate-form-input"
                value={filterDay}
                onChange={(e) => setFilterDay(e.target.value)}
                disabled={!filterMonth}
              >
                <option value="">All Days</option>
                {dayOptions.map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div className="affiliate-order-filter-actions">
              <button type="button" className="affiliate-order-filter-clear" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>

          <div className="affiliate-stat-grid">
            {[
              { label: 'Total Orders', value: stats.totalOrders },
              { label: 'Delivered', value: stats.deliveredOrders },
              { label: 'Pending', value: stats.pendingOrders },
              { label: 'Cancelled & Returned', value: stats.cancelledOrders, muted: true },
              { label: 'Total Sales', value: `Rs ${stats.totalSales.toLocaleString()}` },
              { label: 'Commission', value: `Rs ${(stats.totalCommission || 0).toLocaleString()}` },
              { label: 'Total Clicks', value: stats.totalClicks || 0 },
            ].map(({ label, value, muted }) => (
              <div key={label} className="affiliate-stat-card">
                <div className="affiliate-stat-label">{label}</div>
                <div className={`affiliate-stat-value${muted ? ' affiliate-stat-value--muted' : ''}`}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ ...styles.sectionTitle, color: '#D4AF37', marginBottom: '14px' }}>Order Tracking</h2>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Order #', 'Customer', 'Products', 'Total', 'Commission', 'Order Status', 'Date'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={styles.emptyCell}>Loading orders...</td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={styles.emptyCell}>No orders found for this period.</td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order._id}>
                      <td style={styles.td}>{order.orderNumber}</td>
                      <td style={styles.td}>
                        {order.customerId?.name || '—'}
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                          {order.customerId?.email}
                        </div>
                      </td>
                      <td style={styles.td}>
                        {order.items?.map((item, index) => (
                          <div key={index}>
                            {item.productId?.name || 'Product'} × {item.quantity}
                          </div>
                        ))}
                      </td>
                      <td style={styles.td}>Rs {order.totalAmount?.toLocaleString()}</td>
                      <td style={styles.td}>Rs {(order.commissionAmount || 0).toLocaleString()}</td>
                      <td style={{ ...styles.td, color: orderStatusColor[order.orderStatus] || '#E5D0A5', fontWeight: 600 }}>
                        {order.orderStatus}
                      </td>
                      <td style={styles.td}>{new Date(order.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
